const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const clamp = (num, min, max) => Math.min(max, Math.max(min, num));

const scoreToRating = (score) => {
  if (score < 3.5) return 'Poor';
  if (score < 5.0) return 'Weak';
  if (score < 6.5) return 'Fair';
  if (score < 8.0) return 'Good';
  return 'Strong';
};

const collectText = (json = {}) => {
  const parts = [];
  const push = (v) => { if (v) parts.push(String(v)); };
  push(json?.executive_summary?.headline);
  push(json?.executive_summary?.summary);
  push(json?.overall_health?.rationale);
  push(json?.consultant_note);
  (json?.whats_not_working || []).forEach((x) => { push(x?.title); push(x?.evidence); push(x?.interpretation); });
  (json?.major_discovery_rules || []).forEach((x) => { push(x?.title); push(x?.evidence); push(x?.interpretation); push(x?.business_impact); });
  (json?.headline_signals || []).forEach((x) => { push(x?.label); push(x?.why_it_matters); push(x?.value); });
  return parts.join(' | ').toLowerCase();
};

const countMatches = (text, patterns) => patterns.reduce((acc, p) => acc + (p.test(text) ? 1 : 0), 0);

const deriveNarrativeCap = (json = {}, payload = {}) => {
  const text = collectText(json);
  const severePatterns = [
    /\bextreme\b/, /\bsevere\b/, /\bdreadful\b/, /\bvery low\b/, /\bexcessive\b/,
    /\bhighly fragmented\b/, /\bextreme process variation\b/, /\bno standard process\b/,
    /\btoo complex\b/, /\bunpredictable\b/, /\bchaotic\b/, /\bbroken\b/
  ];
  const moderatePatterns = [
    /\bhigh\b/, /\belevated\b/, /\bmeaningful\b/, /\blow flow efficiency\b/, /\bfrequent rework\b/
  ];
  const severeCount = countMatches(text, severePatterns);
  const moderateCount = countMatches(text, moderatePatterns);

  const ch = payload?.canonical_health || {};
  const drivers = Array.isArray(ch?.top_drivers) ? ch.top_drivers.join(' | ').toLowerCase() : '';
  const driverSevereCount = countMatches(drivers, severePatterns);
  const canonicalScore = toNumber(ch?.score);

  let cap = 10;
  if (severeCount >= 4) cap = 4.4;
  else if (severeCount >= 3) cap = 4.8;
  else if (severeCount >= 2) cap = 5.3;
  else if (moderateCount >= 4) cap = 5.8;

  if (driverSevereCount >= 2 && canonicalScore !== null) {
    cap = Math.min(cap, canonicalScore + 0.6);
  } else if (driverSevereCount >= 1 && canonicalScore !== null) {
    cap = Math.min(cap, canonicalScore + 0.9);
  }

  const processSummary = payload?.process_summary || {};
  const time = payload?.time_intelligence || {};
  const variants = toNumber(processSummary?.variants_total);
  const items = toNumber(processSummary?.total_items);
  const rework = toNumber(processSummary?.rework_rate_percent);
  const flow = toNumber(time?.flow_efficiency_percent);
  const delay = Math.max(toNumber(time?.avg_queue_time_hours) || 0, toNumber(time?.avg_admin_time_hours) || 0);
  const variantDensity = variants && items ? (variants / items) * 100 : null;

  if ((flow !== null && flow < 10 && rework !== null && rework >= 30) || (variantDensity !== null && variantDensity >= 25 && delay >= 500)) {
    cap = Math.min(cap, 4.0);
  } else if ((flow !== null && flow < 12) || (variantDensity !== null && variantDensity >= 20) || (delay >= 500) || (rework !== null && rework >= 35)) {
    cap = Math.min(cap, 4.8);
  }

  return { cap, severeCount, moderateCount, driverSevereCount };
};

export const validateHealthScoreAgainstNarrative = (json = {}, payload = {}) => {
  const score = toNumber(json?.overall_health?.score);
  if (score === null) return json;

  const meta = deriveNarrativeCap(json, payload);
  let nextScore = score;
  if (score > meta.cap) nextScore = meta.cap;
  nextScore = clamp(Math.round(nextScore * 10) / 10, 1.0, 10.0);

  const overall = {
    ...json.overall_health,
    score: nextScore,
    score_display: nextScore,
    rating: scoreToRating(nextScore),
  };

  if (nextScore < score) {
    const note = `Score adjusted to ${nextScore}/10 to stay aligned with the severity of the assessment narrative.`;
    overall.rationale = overall.rationale ? `${overall.rationale} ${note}` : note;
  }

  return { ...json, overall_health: overall };
};
